import React, { createContext, ReactNode, useEffect, useState } from 'react';
import { devBypassMockUser, isDevAuthBypassEnabled } from '../auth/devAuthBypass';

// Define the types for permissions and user
type CrudPermissions = {
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
};

type Permissions = {
  [key: string]: CrudPermissions;
};

export type User = {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  permissions: Permissions;
  /** Analytic profile IDs assigned to this user (from the API). */
  analytic_profiles?: string[];
};

// Define the context value type
type UserContextType = {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
};

// Create the context with a default value
export const UserContext = createContext<UserContextType>({
  user: null,
  setUser: () => {},
});

// Define the props for the UserProvider
type UserProviderProps = {
  children: ReactNode;
};

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    if (isDevAuthBypassEnabled()) {
      return devBypassMockUser as User;
    }
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        return JSON.parse(storedUser) as User;
      }
    } catch (error) {
      console.error('Failed to parse user data:', error);
    }
    return null;
  });

  useEffect(() => {
    // Check if data is invalid and reload guard isn't active
    const storedUser = localStorage.getItem('user');
    if (!user && storedUser && !localStorage.getItem('reloadGuard')) {
      console.warn('Invalid data detected. Clearing and reloading.');
      localStorage.setItem('reloadGuard', 'true');
      localStorage.removeItem('user');
      window.location.reload();
    }

    // Clear reload guard after reload
    return () => {
      localStorage.removeItem('reloadGuard');
    };
  }, [user]);

  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }, [user]);

  return (
    <UserContext.Provider value={{ user, setUser }}>
      {children}
    </UserContext.Provider>
  );
};